import { ChangeEvent, useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { Button, Grid, TextField } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { ru } from 'date-fns/locale/ru'
import { CourseBlockState, Video, VideoSettingsState } from 'features/videos/service'
import { restoreVideoSettingsState } from 'features/videos/utils'
import { Course, CourseTypes } from 'services/courses'
import { generateVideoUrlFromId, parseVideoUrl } from 'utils/parseVideoUrl'

import s from './VideoMainForm.module.css'

import { CodeController } from './controllers/code-controller/CodeController'
import { DescriptionController } from './controllers/description-controller/DescriptionController'
import { HostingController } from './controllers/hosting-controller/HostingController'
import { MentorController } from './controllers/mentor-controller/MentorController'
import { NameController } from './controllers/name-controller/NameController'
import { PublicVisibilityController } from './controllers/public-visibility-controller/PublicVisibilityController'
import { SeoDescriptionController } from './controllers/seo-description-controller/SeoDescriptionController'
import { SlugController } from './controllers/slug-controller/SlugController'
import { StartDateController } from './controllers/start-date-controller/StartDateController'
import { VideoUrlController } from './controllers/video-url-controller/VideoUrlController'
import { VideoSettings } from './video-settings/VideoSettings'

type Props = {
  courses?: Course[]
  defaultValues?: Video
  isLoading: boolean
  submitHandler: SubmitHandler<Video>
}

const createEmptyBlock = (): CourseBlockState => ({
  id: crypto.randomUUID(),
  courseId: null,
  selectedPlaylistsIds: [],
  tabValue: false,
})

export const VideoMainForm = ({ courses, defaultValues, isLoading, submitHandler }: Props) => {
  const [videoUrl, setVideoUrl] = useState('')
  const [courseBlocks, setCourseBlocks] = useState<CourseBlockState[]>([createEmptyBlock()])
  const [videoSettingsState, setVideoSettingsState] = useState<VideoSettingsState>({})

  const {
    clearErrors,
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    reset,
    setError,
    setValue,
  } = useForm<Video>({
    mode: 'all',
  })

  const changeUrl = (event: ChangeEvent<HTMLInputElement>) => {
    parseVideoUrl(event, setVideoUrl, setValue, getValues, setError, clearErrors)
  }

  const checkSelectedPlaylistsRequiredSettings = () => {
    for (const playlistId in videoSettingsState) {
      if (videoSettingsState[playlistId].length === 0) {
        // Find which block contains this playlist and set its tabValue
        const block = courseBlocks.find(b => b.selectedPlaylistsIds.includes(playlistId))

        if (block) {
          setCourseBlocks(prev =>
            prev.map(b => (b.id === block.id ? { ...b, tabValue: playlistId } : b))
          )
        }

        return true
      }
    }
  }

  const isPublicCourseSelected = (): boolean => {
    return courseBlocks.some(block => {
      if (!block.courseId) {
        return false
      }
      const course = courses?.find(c => c.id === block.courseId)

      return course?.type === CourseTypes.Public
    })
  }

  const onSubmit = (videoModel: Video) => {
    if (checkSelectedPlaylistsRequiredSettings()) {
      return
    }

    if (isPublicCourseSelected() && !videoModel.slug) {
      setError('slug', { type: 'required' })

      return
    }

    const settings = Object.values(videoSettingsState).flat()
    const tags = defaultValues ? defaultValues.tags : []

    submitHandler({ ...videoModel, settings, tags })
  }

  const submitButtonDisable =
    !!errors.hostingId || !!errors.hostingHash || !!errors.name || isLoading

  useEffect(() => {
    if (defaultValues) {
      const { hosting, hostingHash, hostingId, settings, url } = defaultValues

      const defaultVideoUrl = generateVideoUrlFromId(hosting, hostingId, hostingHash, url)

      setVideoUrl(defaultVideoUrl)

      // Group settings by courseId to create blocks
      const settingsByCourse = settings.reduce(
        (acc, setting) => {
          const key = setting.courseId

          if (!acc[key]) {
            acc[key] = []
          }
          acc[key].push(setting)

          return acc
        },
        {} as Record<number, typeof settings>
      )

      const blocks: CourseBlockState[] = Object.entries(settingsByCourse).map(
        ([courseId, courseSettings]) => {
          const playlistIds = Array.from(new Set(courseSettings.map(s => s.playlistId)))

          return {
            id: crypto.randomUUID(),
            courseId: Number(courseId),
            selectedPlaylistsIds: playlistIds,
            tabValue: playlistIds[0] || false,
          }
        }
      )

      if (blocks.length > 0) {
        setCourseBlocks(blocks)
      }

      const allPlaylistIds = Array.from(new Set(settings.map(s => s.playlistId)))

      const defaultVideoSettingsState = restoreVideoSettingsState(allPlaylistIds, settings)

      setVideoSettingsState(defaultVideoSettingsState)

      reset(defaultValues)
    }
  }, [defaultValues, reset])

  return (
    <form className={s.container} onSubmit={handleSubmit(onSubmit)}>
      <LocalizationProvider adapterLocale={ru} dateAdapter={AdapterDateFns}>
        <NameController control={control} />
        <SlugController control={control} />
        <PublicVisibilityController control={control} />

        <DescriptionController control={control} />

        <SeoDescriptionController control={control} />

        <HostingController
          clearErrors={clearErrors}
          control={control}
          setValue={setValue}
          setVideoUrl={setVideoUrl}
        />

        <TextField
          fullWidth
          label={'Ссылка на видео'}
          onChange={changeUrl}
          required
          value={videoUrl}
        />

        <VideoUrlController control={control} name={'hostingId'} />

        <VideoUrlController control={control} name={'hostingHash'} />

        <div className={s.flexContainer}>
          <MentorController control={control} setValue={setValue} />

          <StartDateController control={control} setValue={setValue} />

          <CodeController control={control} />
        </div>

        <VideoSettings
          courseBlocks={courseBlocks}
          courses={courses}
          setCourseBlocks={setCourseBlocks}
          setVideoSettingsState={setVideoSettingsState}
          videoSettingsState={videoSettingsState}
        />

        <Grid container justifyContent={'flex-end'} marginTop={4}>
          <Button disabled={submitButtonDisable} type={'submit'}>
            Сохранить
          </Button>
        </Grid>
      </LocalizationProvider>
    </form>
  )
}
